import { useMemo, useState, useEffect } from 'react';
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import { useDCADynamicModel } from '../../hooks/useDCADynamicModel';
import { getAlloyMboxParamters, getMboxParamters } from '../../lib/helpers';
import { IReturnTypeOFGetMboxParam, IUpdatedPageModel, MboxExtractionContext } from '../../types';
import { useDCAAlloyModel } from '../../hooks/useDCAAlloyModel';
import { DCAPayloadConfig } from '../../lib/dynamicContentAssemblyUtils/targetSDKPayloadV2';
import { StyledDcaWrapper } from './StyledDcaWrapper.styles';

const getPageModelCopy = (pageModel: Record<string, unknown> = {}) => {
  try {
    // By doing will get exact copy of pageModel even nested parameretes will copied properly
    return JSON.parse(JSON.stringify(pageModel));
  } catch (e) {
    return pageModel;
  }
};

/**
 *
 * @param Component referece of a page
 * @param pageProps pageprps of page
 * @param initialMboxExtractionContext optional override to inject an allow-list `mboxList` for a specific componentType and filter out other mboxes
 * @description this provider, will do single dca call, by taking the pagemodel
 * from pageProps, and then store the result in DCAPageModelStore
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DynamicDCAPageProvider = ({
  Component,
  pageProps,
  initialMboxExtractionContext,
  dcaConfig,
  queryOverride,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pageProps: any;
  initialMboxExtractionContext?: MboxExtractionContext;
  dcaConfig?: DCAPayloadConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryOverride?: any;
}) => {
  const pageModel = useMemo(() => getPageModelCopy(pageProps?.model), [pageProps?.model]);

  const { mboxParameters, enableBannerTriggerLogic } = useMemo(
    () => getMboxParamters(pageModel, initialMboxExtractionContext),
    [pageModel, initialMboxExtractionContext]
  );
  const alloyTargetMboxes = useMemo(() => getAlloyMboxParamters(pageModel), [pageModel]);

  useDCADynamicModel(mboxParameters, pageModel, enableBannerTriggerLogic, dcaConfig, queryOverride);
  useDCAAlloyModel(alloyTargetMboxes);
  return useMemo(() => <Component {...pageProps} />, [Component, pageProps]);
};

/**
 *
 * @param props - refers to component props
 * @param Component - refers to DCA component
 * @description this generic component provider will provide uxl response to the component i.e.,
 * once uxl call happens then this provider will return updated props to component, based on the mboxParameter...
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DynamicDCAComponentProvider = ({ props, Component }: any) => {
  const [dynamicProps, setDynamicProps] = useState(props);
  const [isPropsUpdated, setIsPropsUpdated] = useState(false);
  const updatedPageModel = useDCAPageModelStore(state => state.pageModel) as IUpdatedPageModel;

  useEffect(() => {
    setDynamicProps(props);
  }, [JSON.stringify(props)]);

  useEffect(() => {
    const mboxParameter = dynamicProps?.model?.mboxParameter;
    const isContainerComponent = dynamicProps?.model?.isContainerComponent;

    if (updatedPageModel?.mboxData?.length && !!mboxParameter && !isPropsUpdated && !isContainerComponent) {
      const { dcaResponse, uxlResponse } =
        updatedPageModel?.mboxData?.find(item => item?.mboxName === mboxParameter && !!item?.dcaResponse) ||
        ({} as IReturnTypeOFGetMboxParam);

      if (dcaResponse) {
        setIsPropsUpdated(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setDynamicProps((prev: any) => ({
          ...prev,
          model: { ...dcaResponse },
          ...dcaResponse,
          uxlResponse,
        }));
      }
    }
  }, [updatedPageModel, dynamicProps]);
  return useMemo(
    () => (
      <StyledDcaWrapper
        data-experiment-name={dynamicProps?.responseTokens?.['activity.name']}
        data-variant={dynamicProps?.responseTokens?.['option.name']}
        data-testid="dca-component"
      >
        <Component {...dynamicProps} />
      </StyledDcaWrapper>
    ),
    [dynamicProps]
  );
};
